import {
  AutocompleteMenuSelector,
  ChainOption,
  FormGroup,
  Input,
  ToolTip,
} from '@starsoft/common/components';
import { ChainInput } from '@starsoft/common/components/AutocompleteMenuSelector/CustomOptions/ChainInput';
import {
  useDebounce,
  useTranslation,
  useWithdrawForm,
} from '@starsoft/common/hooks';
import useFormattedCryptoAddress from '@starsoft/common/hooks/useFormattedCryptoAddress';
import { Chain } from '@starsoft/common/models';
import { ChainIDType } from '@starsoft/common/models/chain/id';
import { ErrorService } from '@starsoft/common/services';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { WithdrawFormCreateDto } from '../../../../../../../../dto/withdrawals/baseWithdrawalInputDto/withdraw-create-form.dto';
import styles from './styles.module.scss';

/**
 * TransactionsModalsWithdrawContentFormCryptoContent Component
 * This component handles the rendering of the crypto withdrawal form content.
 * It manages the selection of blockchain networks and the input of crypto addresses.
 *
 * @returns {JSX.Element} The rendered component containing form groups for network selection and address input.
 *
 * @example
 * // Usage example:
 * <TransactionsModalsWithdrawContentFormCryptoContent />
 */
export default function TransactionsModalsWithdrawContentFormCryptoContent() {
  // Retrieve the translation function for the 'withdraw' namespace
  const { t } = useTranslation('withdraw');

  // State to toggle the display of the formatted address
  const [showFormatedAddress, setShowFormatedAddress] =
    useState<boolean>(false);

  // Retrieve form context methods for watching and setting form values
  const {
    watch,
    register,
    formState: { dirtyFields },
    setFocus,
    setValue,
  } = useFormContext<WithdrawFormCreateDto>();

  // Watch the 'address' field in the form
  const address: string = watch('crypto.address');
  // Format the address using a custom hook
  const formattedAddress = useFormattedCryptoAddress(address ?? '');
  // Debounce the address input to reduce unnecessary updates
  const debouncedAddress: string = useDebounce(address);

  // Retrieve withdrawal form state and methods
  const {
    chainOptions,
    isFormLoading,
    accountByCoinQuery: { currentAccount, isLoadingAccountByCoin },
    tokenLookupQuery: { errorTokenLookup, isPendingTokenLookup, tokenLookup },
  } = useWithdrawForm(watch, setValue);

  // Determine if the form is in a loading state
  const isLoading: boolean = isLoadingAccountByCoin || isFormLoading;
  // Watch the 'chainId' field in the form
  const chainId = watch('crypto.chainId');

  /**
   * handleSelectChain Function
   * Updates the selected blockchain network in the form.
   *
   * @param {Chain} chain - The chain object selected by the user.
   * @returns {void}
   *
   * @example
   * handleSelectChain({ id: 1, name: 'Ethereum' });
   */
  function handleSelectChain(chain: Chain) {
    setValue('crypto.chainId', chain?.id as ChainIDType, {
      shouldDirty: true,
    });
  }

  /**
   * handePasteAddress Function
   * Pastes the address from the clipboard into the form.
   *
   * @returns {void}
   *
   * @example
   * handePasteAddress();
   */
  function handePasteAddress() {
    if (typeof navigator == 'undefined') {
      return;
    }

    navigator.clipboard.readText().then(clipText => {
      setValue('crypto.address', clipText);
    });
  }

  // Callback to handle focus on the address input
  const handleFocus = useCallback(() => {
    setShowFormatedAddress(false);
    setTimeout(() => setFocus('crypto.address'));
  }, [setFocus]);

  // Callback to handle blur on the address input
  const handleBlur = useCallback(() => {
    setShowFormatedAddress(true);
  }, []);

  // Render the component with form groups for network selection and address input
  return (
    <div className={styles.container}>
      {chainOptions.length > 1 && (
        <FormGroup
          label={t('crypto_network')}
          isLoading={isLoading}
          loading={{
            label: true,
            secondary: false,
          }}
        >
          <AutocompleteMenuSelector
            hideSearch
            title={t('fiat_form_coin_menu_title')}
            options={chainOptions || []}
            filterBy={['name', 'symbol']}
            loading={isLoading}
            value={chainId}
            isContainerColor
            getOptionLabel={option => option.name}
            getOptionValue={option => option.id}
            setValue={handleSelectChain}
            customComponents={{
              customOption: ({ handleSelect, option, selected }) => (
                <ChainOption
                  option={option}
                  selected={selected}
                  handleSelect={handleSelect}
                />
              ),
              customInput: props => <ChainInput {...props} />,
            }}
          />
        </FormGroup>
      )}
      <FormGroup
        label={t('address_placeholder', {
          currency: currentAccount?.coin?.code,
        })}
        isLoading={isLoading}
        loading={{
          label: true,
          secondary: false,
        }}
      >
        <div className={styles.container__input}>
          <Input
            placeholder={t('address_placeholder')}
            name="crypto.address"
            isLoading={isLoading}
            register={register}
            isContainerColor
            onFocus={handleFocus}
            onBlur={handleBlur}
            focusOnMount
            useTextArea
            value={showFormatedAddress ? formattedAddress : address}
            className={styles.container__input__content}
            registerOptions={{
              required: true,
            }}
            isError={
              dirtyFields?.['crypto.address'] &&
              !isPendingTokenLookup &&
              debouncedAddress === address &&
              (!tokenLookup?.valid || !!errorTokenLookup)
            }
            errorMessage={
              ErrorService.extractError(errorTokenLookup) ??
              (t('invalid_adress_error') as string)
            }
            hasPaddingLeft={false}
            hasPaddingRight
            customIcon={
              <ToolTip titleComponent={t('paste_address')}>
                <i
                  className={`${
                    isPendingTokenLookup
                      ? 'fas fa-circle-notch fa-spin'
                      : 'fa-solid fa-paste'
                  } ${styles.container__input__icon}`}
                  onClick={handePasteAddress}
                />
              </ToolTip>
            }
          />
        </div>
      </FormGroup>
    </div>
  );
}
